/** ОДЗ для школьного графика: интервалы и выколотые x. */

export type DomainInterval = {
  lo: number;
  hi: number;
  loOpen: boolean;
  hiOpen: boolean;
};

export type GraphDomain = {
  intervals: DomainInterval[];
  exclude: number[];
};

const ALL_REALS: GraphDomain = { intervals: [], exclude: [] };

function parseNum(raw: string): number {
  let s = raw.trim().toLowerCase().replace(/\s+/g, "").replace(",", ".");
  if (s === "inf" || s === "+inf" || s === "∞" || s === "+∞") return Infinity;
  if (s === "-inf" || s === "-∞") return -Infinity;
  if (s === "pi" || s === "π") return Math.PI;
  if (s === "-pi" || s === "-π") return -Math.PI;
  const n = Number(s);
  return Number.isFinite(n) ? n : NaN;
}

function splitUnions(src: string): string[] {
  // Объединение интервалов: ; или , или ∪ / U / «или» (внутри скобок ; — граница концов).
  const s = src.replace(/или/gi, "∪").replace(/\bU\b/g, "∪");
  const parts: string[] = [];
  let buf = "";
  let depth = 0;
  for (let i = 0; i < s.length; i++) {
    const c = s[i]!;
    if (c === "(" || c === "[") depth += 1;
    else if (c === ")" || c === "]") depth = Math.max(0, depth - 1);
    if (depth === 0 && (c === "∪" || c === "|" || c === ";" || c === ",")) {
      if (buf.trim()) parts.push(buf.trim());
      buf = "";
      continue;
    }
    buf += c;
  }
  if (buf.trim()) parts.push(buf.trim());
  return parts;
}

function parsePart(raw: string): GraphDomain | null {
  const s = raw
    .trim()
    .replace(/≠/g, "!=")
    .replace(/≤/g, "<=")
    .replace(/≥/g, ">=");

  const bracket = s.match(/^\s*([\(\[])\s*([^)\]]+?)\s*[;,]\s*([^)\]]+?)\s*([\)\]])\s*$/);
  if (bracket) {
    const lo = parseNum(bracket[2]!);
    const hi = parseNum(bracket[3]!);
    if (!Number.isFinite(lo) && lo !== -Infinity) return null;
    if (!Number.isFinite(hi) && hi !== Infinity) return null;
    return {
      intervals: [
        {
          lo,
          hi,
          loOpen: bracket[1] === "(",
          hiOpen: bracket[4] === ")",
        },
      ],
      exclude: [],
    };
  }

  const neq = s.match(/^\s*x\s*!=\s*(.+)\s*$/i);
  if (neq) {
    const n = parseNum(neq[1]!);
    if (!Number.isFinite(n)) return null;
    return { intervals: [], exclude: [n] };
  }

  const two = s.match(/^\s*(.+?)\s*(<=|<)\s*x\s*(<=|<)\s*(.+)\s*$/i);
  if (two) {
    const lo = parseNum(two[1]!);
    const hi = parseNum(two[4]!);
    if (!Number.isFinite(lo) && lo !== -Infinity) return null;
    if (!Number.isFinite(hi) && hi !== Infinity) return null;
    return {
      intervals: [{ lo, hi, loOpen: two[2] === "<", hiOpen: two[3] === "<" }],
      exclude: [],
    };
  }

  const oneXLeft = s.match(/^\s*x\s*(<=|>=|<|>)\s*(.+)\s*$/i);
  if (oneXLeft) {
    const n = parseNum(oneXLeft[2]!);
    if (!Number.isFinite(n) && n !== Infinity && n !== -Infinity) return null;
    const op = oneXLeft[1]!;
    if (op === ">=" || op === ">") {
      return { intervals: [{ lo: n, hi: Infinity, loOpen: op === ">", hiOpen: true }], exclude: [] };
    }
    return { intervals: [{ lo: -Infinity, hi: n, loOpen: true, hiOpen: op === "<" }], exclude: [] };
  }

  const oneXRight = s.match(/^\s*(.+?)\s*(<=|>=|<|>)\s*x\s*$/i);
  if (oneXRight) {
    const n = parseNum(oneXRight[1]!);
    if (!Number.isFinite(n) && n !== Infinity && n !== -Infinity) return null;
    const op = oneXRight[2]!;
    if (op === "<=" || op === "<") {
      return { intervals: [{ lo: n, hi: Infinity, loOpen: op === "<", hiOpen: true }], exclude: [] };
    }
    return { intervals: [{ lo: -Infinity, hi: n, loOpen: true, hiOpen: op === ">" }], exclude: [] };
  }

  return null;
}

export function parseDomain(raw: string): GraphDomain {
  const src = raw.trim();
  if (!src) return ALL_REALS;
  const merged: GraphDomain = { intervals: [], exclude: [] };
  for (const part of splitUnions(src)) {
    const d = parsePart(part);
    if (!d) continue;
    merged.intervals.push(...d.intervals);
    merged.exclude.push(...d.exclude);
  }
  if (!merged.intervals.length && !merged.exclude.length) return ALL_REALS;
  return merged;
}

export function xInDomain(x: number, domain: GraphDomain, eps = 1e-8): boolean {
  if (domain.exclude.some((e) => Math.abs(x - e) <= eps)) return false;
  if (!domain.intervals.length) return true;
  return domain.intervals.some((iv) => {
    if (x < iv.lo - eps || x > iv.hi + eps) return false;
    if (iv.loOpen && x <= iv.lo + eps) return false;
    if (iv.hiOpen && x >= iv.hi - eps) return false;
    return true;
  });
}
